import { bulkDeactivateRiders, BulkOperationResult, bulkReactivateRiders } from '@rideiq/api/services/Riders';
import { QueryParamsForAssignRiders } from '@rideiq/organizers/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  analyticsRidersBulkDeactivateCompleted,
  analyticsRidersBulkReactivateCompleted,
} from '../../api/services/Analytics';
import { ApiResponse } from '../../api/utils/responseTransform';

import { RIDERS_QUERY_KEY } from './useRidersQuery';

interface BulkDeactivateRidersPayload {
  passengerIds: number[] | 'all';
  riderCount: number;
  queryParams?: QueryParamsForAssignRiders;
  reasonKey?: string;
}

interface BulkReactivateRidersPayload {
  passengerIds: number[] | 'all';
  riderCount: number;
  queryParams?: QueryParamsForAssignRiders;
}

const getCompletionOutcome = (response: ApiResponse<{ results: BulkOperationResult[] }>) => {
  if (response.type === 'error') {
    return 'failure';
  }

  const results = response.data.results ?? [];
  const succeededCount = results.filter((result) => result.status === 'success').length;
  const failedCount = results.length - succeededCount;

  if (failedCount === 0) {
    return 'success';
  }
  if (succeededCount === 0) {
    return 'failure';
  }

  return 'partial_failure';
};

export const useBulkRiderMutations = () => {
  const queryClient = useQueryClient();

  const deactivateMutation = useMutation({
    mutationFn: ({ passengerIds, queryParams, reasonKey }: BulkDeactivateRidersPayload) =>
      bulkDeactivateRiders(passengerIds, queryParams, reasonKey),
    onSuccess: (response, { riderCount }) => {
      analyticsRidersBulkDeactivateCompleted({ outcome: getCompletionOutcome(response), rider_count: riderCount });
    },
    onError: (_error, { riderCount }) => {
      analyticsRidersBulkDeactivateCompleted({ outcome: 'failure', rider_count: riderCount });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [RIDERS_QUERY_KEY] });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: ({ passengerIds, queryParams }: BulkReactivateRidersPayload) =>
      bulkReactivateRiders(passengerIds, queryParams),
    onSuccess: (response, { riderCount }) => {
      analyticsRidersBulkReactivateCompleted({ outcome: getCompletionOutcome(response), rider_count: riderCount });
    },
    onError: (_error, { riderCount }) => {
      analyticsRidersBulkReactivateCompleted({ outcome: 'failure', rider_count: riderCount });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [RIDERS_QUERY_KEY] });
    },
  });

  return {
    deactivateRiders: deactivateMutation.mutateAsync,
    reactivateRiders: reactivateMutation.mutateAsync,
  };
};
